import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Table, Badge, Button, Alert, Spinner, Dropdown, ListGroup } from 'react-bootstrap'; // Keep needed imports
import { Link, useParams, useNavigate } from 'react-router-dom';
import { agentApi, notificationApi } from '../../services/api'; // Keep agentApi, notificationApi
import {
  FaArrowLeft,
  FaDownload,
  FaPrint,
  // FaFileInvoice, // Maybe not needed if iframe isn't used
  FaSearch,
  FaTimes,
  FaUser,
  FaSignOutAlt,
  FaBell,
  FaQuestionCircle,
  FaExclamationTriangle // For errors
} from 'react-icons/fa';
import AgentSidebar from '../../components/AgentSidebar'; // Keep AgentSidebar
import { useAuth } from '../../hooks/useAuth'; // Keep useAuth
import './Dashboard.css'; // Keep existing Agent dashboard styles

// --- Helper Functions (Keep Agent's formatting) ---
const formatCurrency = (amount) => {
  // RM format: ₹XX,XX,XXX.XX - Agent format: ₹X,XX,XXX
  // Let's stick to Agent's existing format for consistency within Agent dashboard
  if (amount === null || amount === undefined) return '₹0';
  // Ensure it's a number before formatting
  const numericAmount = Number(amount);
  if (isNaN(numericAmount)) return '₹ N/A';
  return `₹${numericAmount.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const formatDate = (dateString) => {
  // RM format: Month Day, Year - Agent format: Month Day, Year, H:MM AM/PM
  // Let's use a slightly more detailed format than RM, but simpler than Agent's original
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { // Match RM style more closely
      year: 'numeric', month: 'short', day: 'numeric'
    });
  } catch (e) {
    return dateString; // Fallback
  }
};

// Keep Agent's status badge logic
const getApprovalStatusBadge = (status) => {
  switch (status?.toLowerCase()) {
    case 'approved':
      return <Badge bg="success">Approved</Badge>;
    case 'rejected':
      return <Badge bg="danger">Rejected</Badge>;
    case 'pending':
    default:
      return <Badge bg="warning">Pending</Badge>;
  }
};

const getPaymentStatusBadge = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed':
      return <Badge bg="success">Paid</Badge>;
    case 'failed':
      return <Badge bg="danger">Failed</Badge>;
    case 'generated': // Treat 'generated' as the default/initial state before paid/failed
    default:
      return <Badge bg="info">Generated</Badge>;
 }
};

const getPayoutStatusBadge = (status) => { // Payout status might be different from Payment Status
 switch (status?.toLowerCase()) {
   case 'disbursed': // Common term for payout completion
     return <Badge bg="success">Disbursed</Badge>;
   case 'approved': // Approved for payout but not yet sent
     return <Badge bg="info">Approved</Badge>;
   case 'rejected': // Payout rejected
     return <Badge bg="danger">Rejected</Badge>;
   case 'requested': // Initial state
   default:
     return <Badge bg="warning">Requested</Badge>;
 }
};


const InvoiceDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getCurrentUser, logout } = useAuth();
  const currentUser = getCurrentUser();
  const printableRef = useRef(null); // Ref for the printable area

  // State for invoice data (Agent specific)
  const [invoice, setInvoice] = useState(null);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // --- State for Header elements (copied from Agent's previous version) ---
  const [searchQuery, setSearchQuery] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const notificationRef = useRef(null);

  // --- Fetch invoice details (Agent specific) ---
  useEffect(() => {
    const fetchInvoiceDetails = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await agentApi.getInvoiceDetails(id);
        setInvoice(response.data);
        // If the invoice has cases directly, use them. Otherwise, expect them separately or handle absence.
        // Assuming the API response structure includes cases within the invoice details:
        setCases(response.data.cases || []);
      } catch (err) {
        console.error('Error fetching invoice details:', err);
        // More specific error message if possible
        const message = err.response?.data?.message || 'Failed to load invoice details. Please check the invoice ID or try again later.';
        setError(message);
      } finally {
        setLoading(false);
      }
    };
    fetchInvoiceDetails();
  }, [id]);

  // --- Agent actions (Download/Print) ---
  const handleDownload = () => {
    if (invoice && invoice.invoice_url) {
      // Prefer opening the direct URL if available
      window.open(invoice.invoice_url, '_blank');
    } else {
      // Fallback to print if no URL - though print might not be ideal for download
      console.warn('No invoice URL available for download. Consider implementing PDF generation or using print.');
      handlePrint(); // Or show a message
    }
  };

  // Use window.print targeting the ref'd area via CSS
  const handlePrint = () => {
    window.print();
  };

  // --- Handlers for Header elements (copied from Agent's previous version) ---
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleNotifications = () => {
    setShowNotifications(prev => !prev);
  };

  const handleMarkAsRead = async (notificationId) => {
    const notification = notifications.find(n => n.id === notificationId);
    if (!notification || notification.is_read) return;
    try {
      await notificationApi.markAsRead(notificationId);
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    if (unreadCount === 0) return;
    try {
      await notificationApi.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const formatTimestamp = (timestamp) => {
    try {
      return new Date(timestamp).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
    } catch (e) { return timestamp; }
  };

  // --- Notifications polling effect (copied from Agent's previous version) ---
  useEffect(() => {
    let intervalId;
    const fetchNotificationsData = async () => {
      try {
        const countResponse = await notificationApi.getUnreadCount();
        setUnreadCount(countResponse.data.unread_count);
      } catch (error) {
        console.error('Error fetching notifications count:', error);
      }
     };
    fetchNotificationsData(); // Initial fetch
    intervalId = setInterval(fetchNotificationsData, 60000); // Poll count every minute
    return () => clearInterval(intervalId); // Cleanup
  }, []);

  // --- Fetch full notifications when dropdown opens (copied from Agent's previous version) ---
  useEffect(() => {
    const fetchFullNotifications = async () => {
      if (showNotifications) {
        setLoadingNotifications(true);
        try {
          const notificationsResponse = await notificationApi.getNotifications({ limit: 10 });
          setNotifications(notificationsResponse.data);
        } catch (error) {
          console.error('Error fetching notifications:', error);
          setNotifications([]);
        } finally {
          setLoadingNotifications(false);
        }
      }
     };
    fetchFullNotifications();
  }, [showNotifications]);

  // --- Handle clicks outside notification dropdown (copied from Agent's previous version) ---
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

 // --- Loading State ---
 if (loading) {
   return (
      <div className="agent-dashboard-layout">
        <AgentSidebar />
        <div className="agent-content-wrapper">
          {/* Agent Header remains consistent */}
          <header className="agent-header d-print-none">
            {/* ... (Agent header JSX - greeting, search, actions) ... */}
              <div className="agent-greeting">
                  <span className="agent-greeting-text">Hi, {currentUser?.name || 'Agent'} <span className="agent-greeting-emoji">👋</span></span>
              </div>
              <div className="agent-header-actions">
                  {/* ... (Icons, Notifications dropdown, User Profile/Logout - keep exact structure) ... */}
                <div className="agent-header-icon">
                  <FaQuestionCircle size={18} />
                </div>

                <div ref={notificationRef} className="position-relative agent-notifications me-3">
                  <Button
                      variant="link"
                      className="text-secondary p-0 position-relative"
                      onClick={toggleNotifications}
                      id="dropdown-notifications-agent-details"
                      aria-expanded={showNotifications}
                      title="Notifications"
                  >
                      <FaBell size={20} />
                      {unreadCount > 0 && (
                      <Badge
                          pill
                          bg="danger"
                          className="position-absolute top-0 start-100 translate-middle border border-light"
                          style={{ fontSize: '0.65em', padding: '0.3em 0.5em' }}
                      >
                          {unreadCount > 9 ? '9+' : unreadCount}
                          <span className="visually-hidden">unread notifications</span>
                      </Badge>
                      )}
                  </Button>
                  <Dropdown.Menu
                      show={showNotifications}
                      className="shadow mt-2 notification-dropdown"
                      style={{ minWidth: '350px', maxHeight: '450px', overflowY: 'auto' }}
                  >
                      {/* ... (Notification content - keep exact structure) ... */}
                        <Dropdown.Header className="d-flex justify-content-between align-items-center">
                          Notifications
                          {unreadCount > 0 && (
                            <Button variant="link" size="sm" onClick={handleMarkAllAsRead} className="p-0 text-decoration-none">
                              Mark all read
                            </Button>
                          )}
                        </Dropdown.Header>
                        <Dropdown.Divider className="my-1" />
                        {loadingNotifications ? (
                          <div className="text-center p-3">
                            <Spinner animation="border" size="sm" variant="primary" /> Loading...
                          </div>
                        ) : notifications.length > 0 ? (
                          <ListGroup variant="flush">
                            {notifications.map(notif => (
                              <ListGroup.Item
                                key={notif.id}
                                action={!notif.is_read}
                                onClick={() => handleMarkAsRead(notif.id)}
                                style={{
                                  cursor: !notif.is_read ? 'pointer' : 'default',
                                  backgroundColor: !notif.is_read ? '#f8f9fa' : 'transparent',
                                  borderLeft: !notif.is_read ? '4px solid #0d6efd' : 'none',
                                  padding: '0.75rem 1.25rem',
                                  borderBottom: '1px solid #eee'
                                }}
                                className="notification-item"
                              >
                                <div className={`fw-bold ${!notif.is_read ? 'text-primary' : ''}`}>{notif.title || 'Notification'}</div>
                                <div className="text-muted small mb-1">{notif.message || 'No details available.'}</div>
                                <small className="text-muted">{formatTimestamp(notif.created_at)}</small>
                              </ListGroup.Item>
                            ))}
                          </ListGroup>
                        ) : (
                          <Dropdown.ItemText className="text-center text-muted p-3">
                            You're all caught up!
                          </Dropdown.ItemText>
                        )}
                  </Dropdown.Menu>
                </div>
                <div className="agent-user-profile">
                  <div className="agent-avatar">
                    <FaUser />
                  </div>
                  <div className="agent-user-info">
                    <span className="agent-user-name">{currentUser?.name || 'Agent User'}</span>
                    <button className="agent-logout-btn" onClick={handleLogout}><FaSignOutAlt size={14} /><span>Sign out</span></button>
                  </div>
                </div>
              </div>
          </header>
          <Container fluid className="p-4">
            <div className="text-center p-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Loading invoice details...</p>
            </div>
          </Container>
        </div>
      </div>
    );
  }

  // --- Error State ---
  if (error && !invoice) { // Show full page error only if invoice couldn't be loaded at all
    return (
      <div className="agent-dashboard-layout">
        <AgentSidebar />
        <div className="agent-content-wrapper">
          {/* Agent Header remains consistent */}
           <header className="agent-header d-print-none">
              {/* ... (Agent header JSX - greeting, search, actions) ... */}
              <div className="agent-greeting">
                  <span className="agent-greeting-text">Hi, {currentUser?.name || 'Agent'} <span className="agent-greeting-emoji">👋</span></span>
              </div>
              <div className="agent-header-actions">
                  {/* ... (Icons, Notifications dropdown, User Profile/Logout - keep exact structure) ... */}
                <div className="agent-header-icon">
                  <FaQuestionCircle size={18} />
                </div>

                <div ref={notificationRef} className="position-relative agent-notifications me-3">
                  <Button
                      variant="link"
                      className="text-secondary p-0 position-relative"
                      onClick={toggleNotifications}
                      id="dropdown-notifications-agent-details"
                      aria-expanded={showNotifications}
                      title="Notifications"
                  >
                      <FaBell size={20} />
                      {unreadCount > 0 && (
                      <Badge
                          pill
                          bg="danger"
                          className="position-absolute top-0 start-100 translate-middle border border-light"
                          style={{ fontSize: '0.65em', padding: '0.3em 0.5em' }}
                      >
                          {unreadCount > 9 ? '9+' : unreadCount}
                          <span className="visually-hidden">unread notifications</span>
                      </Badge>
                      )}
                  </Button>
                  <Dropdown.Menu
                      show={showNotifications}
                      className="shadow mt-2 notification-dropdown"
                      style={{ minWidth: '350px', maxHeight: '450px', overflowY: 'auto' }}
                  >
                      {/* ... (Notification content - keep exact structure) ... */}
                        <Dropdown.Header className="d-flex justify-content-between align-items-center">
                          Notifications
                          {unreadCount > 0 && (
                            <Button variant="link" size="sm" onClick={handleMarkAllAsRead} className="p-0 text-decoration-none">
                              Mark all read
                            </Button>
                          )}
                        </Dropdown.Header>
                        <Dropdown.Divider className="my-1" />
                        {loadingNotifications ? (
                          <div className="text-center p-3">
                            <Spinner animation="border" size="sm" variant="primary" /> Loading...
                          </div>
                        ) : notifications.length > 0 ? (
                          <ListGroup variant="flush">
                            {notifications.map(notif => (
                              <ListGroup.Item
                                key={notif.id}
                                action={!notif.is_read}
                                onClick={() => handleMarkAsRead(notif.id)}
                                style={{
                                  cursor: !notif.is_read ? 'pointer' : 'default',
                                  backgroundColor: !notif.is_read ? '#f8f9fa' : 'transparent',
                                  borderLeft: !notif.is_read ? '4px solid #0d6efd' : 'none',
                                  padding: '0.75rem 1.25rem',
                                  borderBottom: '1px solid #eee'
                                }}
                                className="notification-item"
                              >
                                <div className={`fw-bold ${!notif.is_read ? 'text-primary' : ''}`}>{notif.title || 'Notification'}</div>
                                <div className="text-muted small mb-1">{notif.message || 'No details available.'}</div>
                                <small className="text-muted">{formatTimestamp(notif.created_at)}</small>
                              </ListGroup.Item>
                            ))}
                          </ListGroup>
                        ) : (
                          <Dropdown.ItemText className="text-center text-muted p-3">
                            You're all caught up!
                          </Dropdown.ItemText>
                        )}
                  </Dropdown.Menu>
                </div>
                <div className="agent-user-profile">
                  <div className="agent-avatar">
                    <FaUser />
                  </div>
                  <div className="agent-user-info">
                    <span className="agent-user-name">{currentUser?.name || 'Agent User'}</span>
                    <button className="agent-logout-btn" onClick={handleLogout}><FaSignOutAlt size={14} /><span>Sign out</span></button>
                  </div>
                </div>
              </div>
          </header>
          <Container fluid className="p-4">
             <Link to="/agent/invoices" className="btn btn-outline-primary mb-3 d-print-none">
                <FaArrowLeft /> Back to Invoices
             </Link>
            <Alert variant="danger" className="text-center">
                <FaExclamationTriangle size={30} className="mb-2" />
                <h5 className="alert-heading">Error Loading Invoice</h5>
                {error}
            </Alert>
          </Container>
        </div>
      </div>
    );
  }

  // --- Main Render - Combined Structure ---
  return (
    <div className="agent-dashboard-layout">
      <AgentSidebar />
      <div className="agent-content-wrapper">
        {/* Agent Header remains consistent */}
         <header className="agent-header d-print-none">
              {/* ... (Agent header JSX - greeting, search, actions) ... */}
              <div className="agent-greeting">
                  <span className="agent-greeting-text">Hi, {currentUser?.name || 'Agent'} <span className="agent-greeting-emoji">👋</span></span>
              </div>
              <div className="agent-header-actions">
                  {/* ... (Icons, Notifications dropdown, User Profile/Logout - keep exact structure) ... */}
                <div className="agent-header-icon">
                  <FaQuestionCircle size={18} />
                </div>

                <div ref={notificationRef} className="position-relative agent-notifications me-3">
                  <Button
                      variant="link"
                      className="text-secondary p-0 position-relative"
                      onClick={toggleNotifications}
                      id="dropdown-notifications-agent-details"
                      aria-expanded={showNotifications}
                      title="Notifications"
                  >
                      <FaBell size={20} />
                      {unreadCount > 0 && (
                      <Badge
                          pill
                          bg="danger"
                          className="position-absolute top-0 start-100 translate-middle border border-light"
                          style={{ fontSize: '0.65em', padding: '0.3em 0.5em' }}
                      >
                          {unreadCount > 9 ? '9+' : unreadCount}
                          <span className="visually-hidden">unread notifications</span>
                      </Badge>
                      )}
                  </Button>
                  <Dropdown.Menu
                      show={showNotifications}
                      className="shadow mt-2 notification-dropdown"
                      style={{ minWidth: '350px', maxHeight: '450px', overflowY: 'auto' }}
                  >
                      {/* ... (Notification content - keep exact structure) ... */}
                        <Dropdown.Header className="d-flex justify-content-between align-items-center">
                          Notifications
                          {unreadCount > 0 && (
                            <Button variant="link" size="sm" onClick={handleMarkAllAsRead} className="p-0 text-decoration-none">
                              Mark all read
                            </Button>
                          )}
                        </Dropdown.Header>
                        <Dropdown.Divider className="my-1" />
                        {loadingNotifications ? (
                          <div className="text-center p-3">
                            <Spinner animation="border" size="sm" variant="primary" /> Loading...
                          </div>
                        ) : notifications.length > 0 ? (
                          <ListGroup variant="flush">
                            {notifications.map(notif => (
                              <ListGroup.Item
                                key={notif.id}
                                action={!notif.is_read}
                                onClick={() => handleMarkAsRead(notif.id)}
                                style={{
                                  cursor: !notif.is_read ? 'pointer' : 'default',
                                  backgroundColor: !notif.is_read ? '#f8f9fa' : 'transparent',
                                  borderLeft: !notif.is_read ? '4px solid #0d6efd' : 'none',
                                  padding: '0.75rem 1.25rem',
                                  borderBottom: '1px solid #eee'
                                }}
                                className="notification-item"
                              >
                                <div className={`fw-bold ${!notif.is_read ? 'text-primary' : ''}`}>{notif.title || 'Notification'}</div>
                                <div className="text-muted small mb-1">{notif.message || 'No details available.'}</div>
                                <small className="text-muted">{formatTimestamp(notif.created_at)}</small>
                              </ListGroup.Item>
                            ))}
                          </ListGroup>
                        ) : (
                          <Dropdown.ItemText className="text-center text-muted p-3">
                            You're all caught up!
                          </Dropdown.ItemText>
                        )}
                  </Dropdown.Menu>
                </div>
                <div className="agent-user-profile">
                  <div className="agent-avatar">
                    <FaUser />
                  </div>
                  <div className="agent-user-info">
                    <span className="agent-user-name">{currentUser?.name || 'Agent User'}</span>
                    <button className="agent-logout-btn" onClick={handleLogout}><FaSignOutAlt size={14} /><span>Sign out</span></button>
                  </div>
                </div>
              </div>
          </header>

        {/* --- Start of Content Area (Adapting RM Structure) --- */}
        <Container fluid className="p-4 invoice-details-page"> {/* Use fluid container */}

            {/* Page Header (RM Style) - Placed within Container */}
            <div className="page-header d-print-none">
                <div className="left-section">
                <Link to="/agent/invoices" className="back-link">
                    <FaArrowLeft /> Back to Invoices
                </Link>
                <h2 className="page-title">Invoice Details</h2>
                {/* Optional: Display invoice number here if needed */}
                {/* <span className="text-muted ms-2">#{invoice?.invoice_no}</span> */}
                </div>
                {/* Agent Actions (Download/Print) */}
                <div className="button-group">
                    {invoice?.invoice_url && (
                        <Button variant="outline-primary" className="action-button" onClick={handleDownload}>
                        <FaDownload className="me-1" /> Download
                        </Button>
                    )}
                    <Button variant="primary" className="action-button print-button" onClick={handlePrint}>
                        <FaPrint className="me-2" /> Print Invoice
                    </Button>
                </div>
            </div>

             {/* Display inline error if fetching succeeded but invoice data is problematic */}
             {error && invoice && (
                <Alert variant="warning" className="mt-3 d-print-none">
                    <FaExclamationTriangle className="me-2" />
                    {error}
                </Alert>
             )}

            {/* Invoice Status Summary (Optional - adapted from Agent's old summary) */}
            <Card className="mb-4 detail-card d-print-none">
                <Card.Header className="detail-card-header">
                    <h4 className="mb-0">Status Summary</h4>
                </Card.Header>
                <Card.Body>
                <Row>
                    <Col xs={6} md={3} className="detail-item">
                    <div className="detail-label">Approval Status</div>
                    <div className="detail-value">{getApprovalStatusBadge(invoice?.invoice_status_str)}</div>
                    </Col>
                    <Col xs={6} md={3} className="detail-item">
                    <div className="detail-label">Payment Status</div>
                    <div className="detail-value">{getPaymentStatusBadge(invoice?.payment_status_str)}</div>
                    </Col>
                    <Col xs={6} md={3} className="detail-item">
                    <div className="detail-label">Payout Status</div>
                    <div className="detail-value">{getPayoutStatusBadge(invoice?.payout_status_str)}</div>
                    </Col>
                    <Col xs={6} md={3} className="detail-item">
                    <div className="detail-label">Case Count</div>
                    <div className="detail-value">{invoice?.case_count ?? cases.length}</div>
                    </Col>
                </Row>
                </Card.Body>
            </Card>


            {/* --- Printable Invoice Section (Adopted from RM) --- */}
            {invoice ? (
                <div ref={printableRef} className="printable-invoice">
                    {/* Container removed from inside ref, use outer fluid container */}
                    <Card className="invoice-card p-4 shadow-sm">
                        {/* Invoice Header (Logo, Title, Meta) */}
                        <div className="invoice-title-section">
                        <div className="company-logo">
                            {/* Use a static or dynamically fetched logo */}
                            <img src="/static/images/Wishfin-logo.png" alt="Company Logo" className="logo-image" />
                        </div>
                        <h2 className="text-center invoice-heading mb-0">INVOICE</h2>
                        <div className="invoice-meta">
                            <div className="invoice-number">
                            <span className="meta-label">Invoice #:</span>
                            <span className="meta-value">{invoice.invoice_no || 'N/A'}</span>
                            </div>
                            <div className="invoice-date">
                            <span className="meta-label">Date:</span>
                            <span className="meta-value">{formatDate(invoice.created_at)}</span>
                            </div>
                            {invoice.payment_date && (
                            <div className="payment-date">
                                <span className="meta-label">Payment Date:</span>
                                <span className="meta-value">{formatDate(invoice.payment_date)}</span>
                            </div>
                            )}
                        </div>
                        </div>

                        {/* Invoice Parties (Sender/Recipient) */}
                        <div className="invoice-parties">
                        <Row>
                            {/* Assuming Company (Wishfin) is Sender, Agent is Recipient */}
                            <Col md={6} className="party-section">
                            <div className="party-header">Sender Details:</div>
                            <div className="party-content">
                                {/* Hardcode or fetch company details */}
                                <p><span className="party-label">Name:</span> Mywish Marketplace Pvt Ltd</p>
                                <p><span className="party-label">Address:</span> E-30, E Block, Sector 8, Noida, Uttar Pradesh 201301</p>
                                <p><span className="party-label">GST No:</span> 06AABCW1234A1Z5</p>
                                {/* Add other relevant company details */}
                            </div>
                            </Col>
                            <Col md={6} className="party-section">
                            <div className="party-header">Recipient Details (Agent):</div>
                            <div className="party-content">
                                {/* Use Agent's details from currentUser or invoice object */}
                                <p><span className="party-label">Name:</span> {invoice.agent_name || currentUser?.name || 'N/A'}</p>
                                <p><span className="party-label">Address:</span> {invoice.agent_address || currentUser?.address || 'N/A'}</p>
                                <p><span className="party-label">Mobile:</span> {invoice.agent_mobile || currentUser?.mobile || 'N/A'}</p>
                                <p><span className="party-label">PAN:</span> {invoice.agent_pan || 'N/A'}</p>
                                {/* Add Agent GSTIN if applicable/available */}
                                {/* <p><span className="party-label">GST No:</span> {invoice.agent_gstin || 'N/A'}</p> */}
                            </div>
                            </Col>
                        </Row>
                        </div>

                        <hr className="invoice-divider" />

                        {/* Invoice Items Table */}
                         {cases && cases.length > 0 ? (
                            <Table bordered responsive className="invoice-items-table">
                            <thead className="table-header">
                                <tr>
                                <th>Case ID</th>
                                <th>Lead Name</th>
                                <th>Product Name</th>
                                <th className="text-center">Application No.</th>
                                <th className="text-end">Payout Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {cases.map((item) => (
                                <tr key={item.id}>
                                    <td>{item.id}</td>
                                    <td>{item.lead_name || 'N/A'}</td>
                                    <td>{item.product_name || 'N/A'}</td>
                                    <td className="text-center">{item.application_no || 'N/A'}</td>
                                    <td className="text-end">{formatCurrency(item.payout_amount)}</td>
                                </tr>
                                ))}
                            </tbody>
                            </Table>
                         ) : (
                            <div className="text-center p-3 text-muted">No case details found for this invoice.</div>
                         )}


                        {/* Invoice Summary (Totals) */}
                        <div className="invoice-summary">
                            <Row className="mb-2">
                                <Col className="text-end">
                                <span className="summary-label">Base Amount:</span>
                                <span className="summary-value">{formatCurrency(invoice.amount)}</span>
                                </Col>
                            </Row>
                             <Row className="mb-2">
                                <Col className="text-end">
                                <span className="summary-label">GST Amount ({invoice.gst_percentage || 18}%):</span>
                                <span className="summary-value">{formatCurrency(invoice.gst_amount)}</span>
                                </Col>
                            </Row>
                            <Row className="mb-2">
                                <Col className="text-end">
                                <span className="summary-label grand-total">Grand Total:</span>
                                <span className="summary-value grand-total">{formatCurrency(invoice.total_amount)}</span>
                                </Col>
                            </Row>
                             {/* Add TDS if applicable and available */}
                             {invoice.tds_amount > 0 && (
                                <Row className="mb-2">
                                    <Col className="text-end">
                                    <span className="summary-label">TDS ({invoice.tds_percentage || 0}%):</span>
                                    <span className="summary-value">-{formatCurrency(invoice.tds_amount)}</span>
                                    </Col>
                                </Row>
                             )}
                              {/* Add Net Payable if different from Grand Total (e.g., after TDS) */}
                             {(invoice.tds_amount > 0 || invoice.net_payable) && (
                                <Row className="mb-2">
                                    <Col className="text-end">
                                    <span className="summary-label grand-total">Net Payable:</span>
                                    <span className="summary-value grand-total">{formatCurrency(invoice.net_payable ?? (invoice.total_amount - (invoice.tds_amount || 0)))}</span>
                                    </Col>
                                </Row>
                             )}
                        </div>

                        <hr className="invoice-divider" />

                        {/* Bank Details (Agent's Bank Details for Payment) */}
                        <div className="bank-details">
                        <h5>Recipient's Bank Details (For Payment):</h5>
                        <div className="bank-details-grid">
                             {/* Map from invoice object */}
                            <p><span className="bank-label">Account Name:</span> {invoice.bank_account_name || 'N/A'}</p>
                            <p><span className="bank-label">Bank Name:</span> {invoice.bank_name || 'N/A'}</p>
                            <p><span className="bank-label">Account Number:</span> {invoice.bank_account_number || 'N/A'}</p>
                            <p><span className="bank-label">IFSC Code:</span> {invoice.bank_ifsc || 'N/A'}</p>
                            <p><span className="bank-label">Branch Name:</span> {invoice.bank_branch || 'N/A'}</p>
                        </div>
                        </div>

                        {/* Invoice Footer */}
                        <div className="invoice-footer">
                        {/* <p className="thank-you">Thank you for your business!</p> */}
                        <p className="footer-note">This is a computer generated invoice, no signature required.</p>
                        </div>
                    </Card>
                </div>
            ) : (
                 // Show only if loading is finished but invoice is still null (and no major error shown above)
                 !loading && !error && <Alert variant="warning">Invoice data could not be displayed.</Alert>
            )}
            {/* --- End of Printable Invoice Section --- */}

        </Container>
        {/* --- End of Content Area --- */}
      </div>

        {/* --- Styles (Merging RM's Invoice Styles with necessary Agent styles) --- */}
        <style jsx>{`
            /* --- Agent Base Styles (Keep relevant ones) --- */
            .agent-dashboard-layout { display: flex; }
            .agent-content-wrapper { flex-grow: 1; background-color: #f8f9fa; /* Light background */ }
            /* Keep Agent Header styles if they were here or in Dashboard.css */

            /* --- Invoice Details Page Specific Styles (Adapted/Merged from RM and Agent) --- */
            .invoice-details-page {
              /* padding: 20px; // Padding from Container fluid */
              background-color: #f8fafc; /* Match RM background */
              /* max-width: 1200px; // RM had max-width, consider if needed */
              /* margin: 0 auto; // RM had margin */
            }

            /* Page Header Styles (Adapted from RM) */
            .page-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 20px;
                flex-wrap: wrap; /* Allow wrapping on smaller screens */
                gap: 15px; /* Add gap for wrapping */
            }

            .left-section {
                display: flex;
                flex-direction: column; /* Stack back link and title */
            }

            .back-link {
                color: #4b5563;
                text-decoration: none;
                margin-bottom: 4px; /* Space between link and title */
                display: inline-flex;
                align-items: center;
                gap: 6px;
                font-size: 0.9rem;
                transition: color 0.2s;
            }
            .back-link:hover { color: #1f2937; }

            .page-title { /* Renamed from RM's h2 style */
                margin: 0;
                font-size: 1.4rem; /* Match RM */
                font-weight: 600;
                color: #111827; /* Match RM */
            }

            .button-group { /* Container for Download/Print buttons */
                display: flex;
                gap: 10px;
                flex-wrap: wrap;
            }
             


            /* Detail Card for Status Summary (Adopted from Agent's old style) */
             .detail-card {
                 box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
                 border: none;
                 border-radius: 8px;
                 background-color: #fff; /* Ensure background */
             }
             .detail-card-header {
                 background-color: #f8fafc;
                 border-bottom: 1px solid #e2e8f0;
                 padding: 12px 16px;
             }
             .detail-card-header h4 {
                 font-size: 1.1rem;
                 font-weight: 600;
                 color: #1e293b;
                 margin-bottom: 0;
             }
             .detail-item {
                margin-bottom: 1rem; /* Spacing between items */
                padding-bottom: 0.5rem; /* Small bottom padding */
             }
            .detail-label {
                font-size: 0.8rem;
                color: #6b7280;
                margin-bottom: 4px;
                display: block;
                font-weight: 500; /* Slightly bolder label */
            }
            .detail-value {
                font-size: 0.95rem;
                font-weight: 500;
                color: #111827;
            }
            /* Add border only between rows on smaller screens */
            @media (max-width: 767px) {
                .detail-item:not(:last-child) {
                    border-bottom: 1px solid #e2e8f0;
                    padding-bottom: 1rem;
                }
            }


            /* --- Printable Invoice Styles (Directly from RM) --- */
            .printable-invoice {
                background-color: #fff; /* Contained within the card */
                margin-top: 20px; /* Add space above invoice card */
            }

            .invoice-card {
                border: 1px solid #e2e8f0;
                border-radius: 8px;
                overflow: hidden; /* Contain shadows/borders */
            }

            .invoice-title-section {
                display: flex;
                justify-content: space-between;
                align-items: flex-start; /* Align items to top */
                margin-bottom: 30px;
                position: relative; /* For absolute heading */
                padding-bottom: 15px; /* Space below heading */
                border-bottom: 1px solid #e5e7eb; /* Separator */
            }

            .company-logo {
                display: flex;
                align-items: center;
                gap: 10px;
            }

            .logo-image {
                height: 40px; /* Adjust as needed */
                width: auto;
                object-fit: contain;
            }

            .invoice-heading {
                /* Positioned absolutely in RM, let's try centered flex as alternative */
                /* position: absolute; */
                /* left: 0; right: 0; top: 0; */
                text-align: center; /* Center if not absolute */
                font-size: 1.8rem;
                font-weight: 700;
                color: #111827;
                text-transform: uppercase;
                letter-spacing: 1px;
                /* If using flex for centering: */
                 flex-grow: 1;
                 align-self: center;
                 margin-top: 5px; /* Adjust vertical position */
            }

            .invoice-meta {
                display: flex;
                flex-direction: column;
                gap: 4px;
                text-align: right;
                min-width: 180px; /* Ensure space for meta */
            }

            .invoice-number, .invoice-date, .payment-date {
                display: flex;
                gap: 8px;
                justify-content: flex-end;
            }

            .meta-label {
                color: #6b7280;
                font-weight: 500;
                font-size: 0.85rem; /* Slightly smaller */
            }

            .meta-value {
                font-weight: 600;
                color: #111827;
                font-size: 0.85rem; /* Slightly smaller */
            }

            .invoice-parties {
                margin-bottom: 20px;
            }

            .party-section {
                margin-bottom: 16px;
            }

            .party-header {
                font-size: 1.0rem; /* Slightly smaller */
                font-weight: 600;
                color: #374151;
                margin-bottom: 10px; /* Reduced space */
                border-bottom: 1px dashed #d1d5db; /* Add subtle separator */
                padding-bottom: 5px;
            }

            .party-content p {
                margin-bottom: 5px; /* Tighter spacing */
                color: #4b5563;
                font-size: 0.9rem; /* Slightly smaller */
            }

            .party-label {
                font-weight: 600;
                color: #374151;
                margin-right: 5px; /* Space after label */
                display: inline-block; /* Ensure proper spacing */
                min-width: 70px; /* Align values somewhat */
            }

            .invoice-divider {
                border-top: 1px solid #e5e7eb;
                margin: 25px 0; /* Consistent margin */
            }

            .invoice-items-table {
                margin-bottom: 25px; /* Space after table */
            }

            .table-header th {
                background-color: #f3f4f6;
                color: #374151;
                font-weight: 600;
                font-size: 0.85rem; /* Slightly smaller */
                text-transform: uppercase; /* Added for clarity */
                letter-spacing: 0.5px; /* Added */
                padding: 10px 12px; /* Adjust padding */
                border-bottom-width: 2px; /* Stronger header border */
            }
             .invoice-items-table tbody td {
                padding: 10px 12px; /* Adjust padding */
                vertical-align: middle;
                font-size: 0.9rem;
                color: #334155;
                border-color: #e5e7eb; /* Lighter cell borders */
            }
             .invoice-items-table tbody tr:last-child td {
                border-bottom: none; /* Remove border on last item row */
             }

            .invoice-summary {
                margin-left: auto;
                width: 100%; /* Full width on small screens */
                max-width: 350px; /* Max width on larger screens */
                font-size: 0.95rem;
            }

            .summary-label {
                font-weight: 500;
                color: #4b5563;
                margin-right: 12px;
                display: inline-block;
            }

            .summary-value {
                font-weight: 500;
                color: #111827;
                display: inline-block;
                min-width: 100px; /* Ensure space for value */
                text-align: right;
            }

            .grand-total {
                font-weight: 700;
                font-size: 1.05rem; /* Slightly larger */
                color: #111827;
            }
            .invoice-summary .row {
                border-bottom: 1px solid #f3f4f6; /* Subtle line between summary items */
                padding-bottom: 5px;
                margin-bottom: 5px !important; /* Override bootstrap margin */
            }
             .invoice-summary .row:last-child {
                border-bottom: none;
             }


            .bank-details {
                background-color: #f9fafb;
                padding: 16px;
                border-radius: 8px;
                margin-top: 20px; /* Space before bank details */
                border: 1px solid #e5e7eb; /* Add subtle border */
            }

            .bank-details h5 {
                font-size: 1.0rem; /* Match party header */
                font-weight: 600;
                color: #374151;
                margin-bottom: 12px;
            }

            .bank-details-grid {
                display: grid;
                grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
                gap: 8px 12px; /* Row and column gap */
            }

            .bank-details-grid p {
                margin-bottom: 4px; /* Tighter spacing */
                color: #4b5563;
                font-size: 0.9rem;
            }

            .bank-label {
                font-weight: 600;
                color: #374151;
                margin-right: 6px;
                display: inline-block;
                min-width: 110px; /* Align values */
            }

            .invoice-footer {
                text-align: center;
                margin-top: 30px;
                padding-top: 15px;
                border-top: 1px solid #e5e7eb;
            }

            .thank-you {
                font-size: 1.1rem;
                font-weight: 600;
                color: #374151;
                margin-bottom: 6px;
            }

            .footer-note {
                font-size: 0.85rem;
                color: #6b7280;
                font-style: italic;
            }

          .print-button {
            background-color: #f3f4f6;
            border-color: #d1d5db;
            color: #111827;
            display: flex;
            align-items: center;
            gap: 5px;
          }
          
          .print-button:hover {
            background-color: #e5e7eb;
          }

            /* --- Print Styles --- */
            @media print {
                body * {
                    visibility: hidden; /* Hide everything by default */
                }
                .printable-invoice, .printable-invoice * {
                    visibility: visible; /* Show only the printable area and its children */
                }
                .printable-invoice {
                    position: absolute;
                    left: 0;
                    top: 0;
                    width: 100%;
                    margin: 0;
                    padding: 0;
                    border: none;
                    box-shadow: none;
                }
                .invoice-card {
                    box-shadow: none !important;
                    border: none !important;
                    padding: 5mm !important; /* Add some padding for print */
                }
                .d-print-none { /* Ensure elements marked this way are hidden */
                    display: none !important;
                 }
                /* Ensure Bootstrap backgrounds/colors print */
                 .table-header th {
                     background-color: #f3f4f6 !important; /* Force background */
                     -webkit-print-color-adjust: exact; /* Chrome, Safari */
                     print-color-adjust: exact; /* Firefox, Edge */
                 }
                /* Hide elements outside the printable area specifically */
                .agent-sidebar, .agent-header, .page-header, .detail-card {
                     display: none !important;
                 }
                 /* Adjust layout for print */
                 .agent-content-wrapper {
                    margin-left: 0 !important;
                    padding: 0 !important;
                 }
                 .container-fluid {
                    padding: 0 !important;
                    max-width: none !important;
                 }
            }
        `}</style>
    </div>
  );
};

export default InvoiceDetails;